import React, { Component, ErrorInfo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Refresh from '@mui/icons-material/Refresh';
import { isViteDev } from '../../config';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Silent error handling

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Log error to monitoring service
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Use the logging service for consistent error logging
      import('../../services/loggingService').then(({ loggingService }) => {
        loggingService.error(
          'React Error Boundary caught error',
          {
            componentStack: errorInfo.componentStack,
            errorBoundary: true,
          },
          error
        );
      });
    } catch (_loggingError) {
      // Silent error handling for logging service
    }
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          p={3}
        >
          <Stack spacing={3} alignItems="center" maxWidth="600px">
            <ErrorOutline color="error" sx={{ fontSize: 64 }} />

            <Typography variant="h4" component="h1" textAlign="center">
              Something went wrong
            </Typography>

            <Typography variant="body1" textAlign="center" color="text.secondary">
              We're sorry, but something unexpected happened. Please try refreshing the page or
              contact support if the problem persists.
            </Typography>

            {isViteDev && this.state.error && (
              <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
                >
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </Typography>
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={this.handleRetry}
              size="large"
            >
              Try Again
            </Button>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export const useErrorHandler = () => {
  const handleError = React.useCallback((error: Error, errorInfo?: React.ErrorInfo) => {
    // Use the logging service for consistent error logging
    import('../../services/loggingService').then(({ loggingService }) => {
      loggingService.error(
        'Error caught by useErrorHandler',
        errorInfo as Record<string, unknown>,
        error
      );
    });
  }, []);

  return { handleError };
};
